import { z } from 'zod';
import type { BattleCommand, BattleRules, CardId, PlayerId } from '../engine/types';

export const NETWORK_PROTOCOL_VERSION = '1.2.0';
export type NetworkSeat = 'seat-a' | 'seat-b';
export const NETWORK_EMOTES = ['thumbs-up', 'well-played', 'wow', 'laugh', 'oops', 'good-luck'] as const;
export type NetworkEmote = typeof NETWORK_EMOTES[number];

export const sideForSeat = (seat: NetworkSeat): PlayerId => seat === 'seat-a' ? 'player' : 'bot';
export const opponentSeat = (seat: NetworkSeat): NetworkSeat => seat === 'seat-a' ? 'seat-b' : 'seat-a';

const playerId = z.enum(['player', 'bot']);
const point = z.object({ x: z.number().finite(), y: z.number().finite() });
const command = z.object({ type: z.literal('play-card'), tick: z.number().int().nonnegative(), sequence: z.number().int().positive(), playerId, cardId: z.string().min(1), position: point });
const checkpoint = z.object({ tick: z.number().int().nonnegative(), checksum: z.string().regex(/^[0-9a-f]{16}$/) });

export const inputBatchSchema = z.object({
  type: z.literal('input-batch'),
  protocolVersion: z.literal(NETWORK_PROTOCOL_VERSION),
  networkMatchId: z.string().min(1),
  seat: z.enum(['seat-a', 'seat-b']),
  sequence: z.number().int().positive(),
  commands: z.array(command).max(4),
  confirmedThroughTick: z.number().int().min(-1),
  checkpoint: checkpoint.optional(),
});

export type InputBatch = Omit<z.infer<typeof inputBatchSchema>, 'commands'> & { readonly commands: readonly BattleCommand[] };

export const emoteMessageSchema = z.object({
  type: z.literal('emote'),
  protocolVersion: z.literal(NETWORK_PROTOCOL_VERSION),
  networkMatchId: z.string().min(1),
  seat: z.enum(['seat-a', 'seat-b']),
  sequence: z.number().int().positive(),
  emote: z.enum(NETWORK_EMOTES),
});
export type EmoteMessage = z.infer<typeof emoteMessageSchema>;

export interface NetworkParticipant {
  readonly seat: NetworkSeat;
  readonly networkPlayerId: string;
  readonly displayName: string;
}

export interface NetworkRoomConfig {
  readonly protocolVersion: typeof NETWORK_PROTOCOL_VERSION;
  readonly networkMatchId: string;
  readonly seed: number;
  readonly engineVersion: string;
  readonly contentVersion: string;
  readonly arenaId: string;
  readonly rules: BattleRules;
  readonly decks: Readonly<Record<PlayerId, readonly CardId[]>>;
  readonly participants: Readonly<Record<NetworkSeat, NetworkParticipant>>;
}

export interface NetworkIdentity {
  readonly schemaVersion: 1;
  readonly networkPlayerId: string;
  readonly playerTicket: string;
}

export interface RoomSession {
  readonly roomId: string;
  readonly roomCode: string;
  readonly networkMatchId: string;
  readonly seat: NetworkSeat;
  readonly seatTicket: string;
  readonly websocketUrl: string;
  readonly inviteToken?: string;
  readonly inviteExpiresAt?: string;
}

export interface InvitePreview {
  readonly hostDisplayName: string;
  readonly expiresAt: string;
  readonly hostRating: { readonly completedMatches: number; readonly wins: number; readonly winRate: number };
  readonly headToHead: { readonly completed: number; readonly hostWins: number; readonly guestWins: number; readonly draws: number } | null;
}

export interface IceServerConfig { readonly iceServers: readonly RTCIceServer[]; readonly relayAvailable: boolean }

export interface RoomStateMessage { readonly type: 'room-state'; readonly roomId: string; readonly status: string; readonly config: NetworkRoomConfig | null }
export interface SignalMessage { readonly type: 'signal'; readonly from: NetworkSeat; readonly data: unknown }
export interface MatchReadyMessage { readonly type: 'match-ready'; readonly networkMatchId: string }
export interface NetworkErrorMessage { readonly type: 'error'; readonly code: string }
export type ServerControlMessage = RoomStateMessage | SignalMessage | MatchReadyMessage | NetworkErrorMessage;

export interface FinalClaim {
  readonly winner: PlayerId | 'draw';
  readonly finalTick: number;
  readonly finalChecksum: string;
  readonly replayHash: string;
}

export interface ClaimStatus {
  readonly status: 'pending' | 'peer-confirmed' | 'disputed' | 'abandoned';
  readonly resultTrust: 'local' | 'peer-confirmed';
}

export interface LeaderboardEntry {
  readonly rank: number;
  readonly displayName: string;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
  readonly completedMatches: number;
  readonly winRate: number;
  readonly trustTier: 'peer-confirmed' | 'server-verified';
}
